<template>
  <header class="admin-header">
    <div class="header-left">
      <div class="breadcrumb">
        <template v-for="(crumb, index) in breadcrumbs" :key="index">
          <span v-if="index > 0" class="breadcrumb-sep">/</span>
          <span
            class="breadcrumb-item"
            :class="{ current: index === breadcrumbs.length - 1 }"
          >{{ crumb }}</span>
        </template>
      </div>
    </div>

    <div class="header-right">
      <input
        type="text"
        class="search-input"
        :placeholder="t('admin.nav.search')"
      />

      <LanguageSwitcher />

      <button class="notif-bell" aria-label="Notifications">
        <AppIcon name="nav-bell" :size="20" />
        <span v-if="notificationCount > 0" class="notif-badge">{{ notificationCount }}</span>
      </button>

      <div class="header-admin-wrap" ref="adminRef">
        <button class="header-admin" @click="toggleDropdown" aria-label="Admin menu">
          <div class="header-admin-avatar">{{ adminInitials }}</div>
          <span class="header-admin-name">{{ adminName }}</span>
        </button>
        <Transition name="dropdown">
          <div v-if="dropdownOpen" class="admin-dropdown">
            <router-link to="/admin/settings" class="dropdown-item" @click="closeDropdown">
              <span class="dropdown-icon"><AppIcon name="nav-user" :size="16" /></span>
              {{ t('nav.profile') }}
            </router-link>
            <router-link to="/admin/settings" class="dropdown-item" @click="closeDropdown">
              <span class="dropdown-icon"><AppIcon name="nav-settings" :size="16" /></span>
              {{ t('nav.settings') }}
            </router-link>
            <div class="dropdown-divider"></div>
            <button class="dropdown-item dropdown-item--danger" @click="handleLogout">
              <span class="dropdown-icon"><AppIcon name="nav-logout" :size="16" /></span>
              {{ t('nav.logout') }}
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminStore } from '@/stores/admin'
import AppIcon from '@/components/AppIcon.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const adminStore = useAdminStore()
const dropdownOpen = ref(false)
const adminRef = ref<HTMLElement | null>(null)

// Stub admin data — will come from a Pinia store once auth is implemented
const adminName = ref('Admin')
const adminInitials = computed(() => {
  return adminName.value
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})
const notificationCount = ref(5)

// Breadcrumbs from route meta
const breadcrumbs = computed(() => {
  const meta = route.meta
  if (meta.breadcrumb && meta.breadcrumb.length > 0) {
    return meta.breadcrumb
  }
  return [meta.title || 'Admin']
})

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function closeDropdown() {
  dropdownOpen.value = false
}

async function handleLogout() {
  closeDropdown()
  await adminStore.logout()
  router.push('/admin/login')
}

function handleClickOutside(e: MouseEvent) {
  if (adminRef.value && !adminRef.value.contains(e.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.admin-header {
  height: var(--header-height, 60px);
  background: rgba(15, 25, 35, 0.96);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 12px;
  z-index: 200;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Breadcrumbs */
.breadcrumb {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  display: flex;
  align-items: center;
  gap: 6px;
}

.breadcrumb-sep {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
}

.breadcrumb-item.current {
  color: var(--text-primary, #fff);
  font-weight: 600;
}

/* Search */
.search-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 8px 12px;
  color: var(--text-primary, #fff);
  font-size: 13px;
  width: 200px;
  transition: border-color var(--transition-fast, 150ms ease);
  font-family: var(--font-body, 'DM Sans', sans-serif);
}

.search-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

/* Notification bell */
.notif-bell {
  width: 34px;
  height: 34px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  position: relative;
  transition: background var(--transition-fast, 150ms ease);
  border: none;
  flex-shrink: 0;
}

.notif-bell:hover {
  background: var(--bg-hover, #2a4455);
}

.notif-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  background: var(--color-danger, #ed4245);
  border-radius: var(--radius-full, 9999px);
  border: 1.5px solid rgba(15, 25, 35, 0.96);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: var(--color-on-dark, #fff);
  font-weight: 700;
  padding: 0 3px;
}

/* Admin profile */
.header-admin-wrap {
  position: relative;
  flex-shrink: 0;
}

.header-admin {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--radius-md, 8px);
  transition: background var(--transition-fast, 150ms ease);
  border: none;
  background: none;
}

.header-admin:hover {
  background: var(--bg-tertiary, #213743);
}

.header-admin-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--gradient-avatar, linear-gradient(135deg, #1475e1, #9b59b6));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-dark, #fff);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.header-admin-name {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

/* Dropdown */
.admin-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
  padding: 6px;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  z-index: 300;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: var(--radius-sm, 4px);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #b1bad3);
  transition: all var(--transition-fast, 150ms ease);
  cursor: pointer;
  text-decoration: none;
  border: none;
  background: none;
  text-align: left;
  font-family: var(--font-body, 'DM Sans', sans-serif);
}

.dropdown-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.dropdown-item--danger:hover {
  color: var(--color-danger, #ed4245);
}

.dropdown-icon {
  font-size: 14px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dropdown-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 4px 0;
}

/* Dropdown transition */
.dropdown-enter-active {
  animation: fadeIn 150ms ease forwards;
}

.dropdown-leave-active {
  animation: fadeOut 100ms ease forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .search-input {
    display: none;
  }

  .header-admin-name {
    display: none;
  }
}

/* =========================================================
   RTL overrides
   ========================================================= */
/* In RTL, the admin menu button ends up on the left side (flex reversal), so anchor dropdown to left */
[dir='rtl'] .admin-dropdown {
  right: auto;
  left: 0;
}

[dir='rtl'] .dropdown-item {
  text-align: right;
}

/* Notification badge: move from top-right to top-left in RTL */
[dir='rtl'] .notif-badge {
  right: auto;
  left: 4px;
}
</style>
